import type { Experiment } from 'types';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  DESTINATION_REQUIRED_MESSAGE,
  isOriginalVariantName,
  ORIGINAL_NAME_TAKEN,
  redirectUrlProblem,
} from 'helpers/variant-fields';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useVariants } from '@/state/experiments/useVariants';
import { apiErrorMessage } from '@/state/api-error';

type AddVariantModalProps = {
  experiment: Experiment;
  open: boolean;
  onClose: () => void;
};

const FIELD_CLASS = 'border-line bg-field text-foreground rounded-lg';

export const AddVariantModal = ({ experiment, open, onClose }: AddVariantModalProps) => {
  const { createVariant } = useVariants(experiment.id);
  const [name, setName] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isUrlRedirect = experiment.type === 'URL Redirect';
  const urlProblem = isUrlRedirect && redirectUrl.trim() ? redirectUrlProblem(redirectUrl.trim()) : undefined;
  const nameIsOriginal = isOriginalVariantName(name);
  const isValid = name.trim() && !nameIsOriginal && (!isUrlRedirect || redirectUrl.trim()) && !urlProblem;

  /**
   * Why the button is greyed out. Two fields can be responsible on a URL Redirect
   * experiment and neither is marked, so a disabled "Add Variant" with no hover text was a
   * dead end - especially with the redirect field only present for that one type.
   */
  const blockedReason = (() => {
    if (isSubmitting) return 'Adding the variant…';
    if (!name.trim()) return 'Enter a variant name first';
    if (nameIsOriginal) return ORIGINAL_NAME_TAKEN;
    if (isUrlRedirect && !redirectUrl.trim()) return DESTINATION_REQUIRED_MESSAGE;
    if (urlProblem) return urlProblem;
    return 'Add this variant';
  })();

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await createVariant({
        name: name.trim(),
        redirectUrl: redirectUrl.trim(),
        experimentId: experiment.id,
        websiteId: experiment.websiteId,
      });
      setName('');
      setRedirectUrl('');
      onClose();
    } catch (error) {
      // Stays open with the typed name intact. There was no catch here at all, so a
      // failure left the dialog open and the button reading "Add Variant" again with
      // nothing said - indistinguishable from a click that never registered.
      toast.error(apiErrorMessage(error, 'Could not add the variant.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setName('');
      setRedirectUrl('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="border-line bg-panel sm:rounded-panel sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-title font-medium">Add Variant</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="variant-name" className="micro-label">
              Variant name
            </Label>
            <Input
              id="variant-name"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              placeholder="e.g., Blue Button"
              className={FIELD_CLASS}
              autoFocus
            />
          </div>

          {isUrlRedirect && (
            <div className="grid gap-2">
              <Label htmlFor="redirect-url" className="micro-label">
                Destination URL
              </Label>
              <Input
                id="redirect-url"
                inputMode="url"
                value={redirectUrl}
                onChange={(ev) => setRedirectUrl(ev.target.value)}
                placeholder="https://example.com/variant-page"
                aria-invalid={!!urlProblem}
                aria-describedby={urlProblem ? 'redirect-url-error' : undefined}
                className={`${FIELD_CLASS} font-mono text-meta`}
              />
              {urlProblem && (
                <p id="redirect-url-error" className="text-danger text-meta">
                  {urlProblem}
                </p>
              )}
            </div>
          )}
        </form>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            title={blockedReason}
            className="bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white"
          >
            {isSubmitting ? 'Adding...' : 'Add Variant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
